import './App.css';
import { useState, useEffect, useRef } from 'react';
import { Navbar }      from './components/Navbar';
import { Hero }        from './components/Hero';
import { About }       from './components/About';
import { Skills }      from './components/Skills';
import { Projects }    from './components/Projects';
import { Timeline }    from './components/Timeline';
import { Credentials } from './components/Credentials';
import { Fun }         from './components/Fun';
import { Contact }     from './components/Contact';
import { Footer }      from './components/Footer';

const MARQUEE_ITEMS = [
  '★ NOW PLAYING ‣ ALIF.GARINDRA.EXE',
  '◇ MOBILE DEVELOPER // REACT-NATIVE',
  '✦ JAKARTA, ID — PRESS START',
  '★ 5+ YEARS ON THE BOARD',
  '◇ ENTERPRISE APPS, SHIPPED',
  '✦ AVAILABLE FOR QUESTS',
];

const PALETTES = [
  { id: 'candy',     colors: ['#ff4fa3','#b98bff','#7ef5c6','#ffe066'] },
  { id: 'bubblegum', colors: ['#ff3d81','#8a6bff','#57e0a8','#ffd447'] },
  { id: 'arcade',    colors: ['#ff4d6d','#6a5cff','#37e6ff','#ffcc33'] },
  { id: 'lime',      colors: ['#ff7ab8','#9a6bff','#9af03c','#f5ff47'] },
];

// ── Animated starfield canvas ─────────────────────────────────────────────
function StarCanvas() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    const ctx = canvas.getContext('2d');
    let stars = [], rafId;

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
      const n = Math.floor(canvas.width * canvas.height / 9000);
      stars = Array.from({ length: n }, () => ({
        x:   Math.random() * canvas.width,
        y:   Math.random() * canvas.height,
        s:   Math.random() * 2 + 1,
        tw:  Math.random() * Math.PI * 2,
        sp:  0.02 + Math.random() * 0.04,
        hue: Math.random() < 0.3 ? 'pink' : (Math.random() < 0.5 ? 'lemon' : 'mint'),
      }));
    };

    const loop = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cs  = getComputedStyle(document.documentElement);
      const pal = {
        pink:  cs.getPropertyValue('--c-pink').trim()  || '#ff4fa3',
        lemon: cs.getPropertyValue('--c-lemon').trim() || '#ffe066',
        mint:  cs.getPropertyValue('--c-mint').trim()  || '#7ef5c6',
      };
      for (const st of stars) {
        st.tw += st.sp;
        ctx.globalAlpha = 0.35 + Math.sin(st.tw) * 0.35;
        ctx.fillStyle   = pal[st.hue] || '#fff';
        ctx.fillRect(Math.round(st.x), Math.round(st.y), st.s, st.s);
      }
      ctx.globalAlpha = 1;
      rafId = requestAnimationFrame(loop);
    };

    resize();
    loop();
    window.addEventListener('resize', resize);
    return () => { cancelAnimationFrame(rafId); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={ref} id="stars" aria-hidden="true" />;
}

// ── Smooth-follow cursor dot ──────────────────────────────────────────────
function CursorDot() {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let cx = window.innerWidth / 2, cy = window.innerHeight / 2;
    let tx = cx, ty = cy, rafId;

    const onMove = (e) => { cx = e.clientX; cy = e.clientY; };
    const onOver = (e) => {
      const hit = e.target.closest('a, button, .cf-card, .orb, .fun-tile, .tl-card, .cred-card');
      el.style.width      = hit ? '28px' : '18px';
      el.style.height     = hit ? '28px' : '18px';
      el.style.background = hit ? 'var(--c-pink)' : 'var(--c-lemon)';
    };
    const loop = () => {
      tx += (cx - tx) * 0.28;
      ty += (cy - ty) * 0.28;
      el.style.transform = `translate(${tx}px,${ty}px) translate(-50%,-50%)`;
      rafId = requestAnimationFrame(loop);
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    document.addEventListener('mouseover', onOver);
    loop();
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener('pointermove', onMove);
      document.removeEventListener('mouseover', onOver);
    };
  }, []);
  return <div ref={ref} id="cursor-dot" aria-hidden="true" />;
}

// ── Top marquee ───────────────────────────────────────────────────────────
function Marquee() {
  const doubled = [...MARQUEE_ITEMS, ...MARQUEE_ITEMS];
  return (
    <div className="top-marquee" role="banner">
      <div className="tm-track">
        {doubled.map((item, i) => <span key={i}>{item}</span>)}
      </div>
    </div>
  );
}

// ── Palette + mode tweaks panel ───────────────────────────────────────────
function TweaksPanel() {
  const [open,    setOpen]    = useState(false);
  const [palette, setPalette] = useState('candy');
  const [mode,    setMode]    = useState('light');

  useEffect(() => {
    const root = document.documentElement;
    if (palette !== 'candy') root.setAttribute('data-palette', palette);
    else root.removeAttribute('data-palette');
    root.setAttribute('data-mode', mode);
  }, [palette, mode]);

  return (
    <>
      <button className="tweaks-toggle" onClick={() => setOpen((o) => !o)} title="Tweaks">🎨</button>
      <div className={`tweaks ${open ? '' : 'hidden'}`}>
        <div className="tw-head">
          <span>◆ TWEAKS</span>
          <button onClick={() => setOpen(false)}>×</button>
        </div>
        <div className="tw-body">
          <div className="tw-row">
            <label>PALETTE</label>
            <div className="tw-palettes">
              {PALETTES.map((p) => (
                <button
                  key={p.id}
                  className={`tw-pal ${palette === p.id ? 'active' : ''}`}
                  title={p.id}
                  onClick={() => setPalette(p.id)}
                >
                  {p.colors.map((c) => <span key={c} style={{ background: c }} />)}
                </button>
              ))}
            </div>
          </div>
          <div className="tw-row">
            <label>MODE</label>
            <div className="tw-modes">
              <button className={`tw-mode ${mode === 'light' ? 'active' : ''}`} onClick={() => setMode('light')}>☀ LIGHT</button>
              <button className={`tw-mode ${mode === 'dark'  ? 'active' : ''}`} onClick={() => setMode('dark')} >☾ DARK</button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ── App ───────────────────────────────────────────────────────────────────
function App() {
  const [isVisible,     setIsVisible]     = useState({});
  const [activeSection, setActiveSection] = useState('home');

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.15 }
    );
    document.querySelectorAll('section[id]').forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="App">
      <div className="crt" aria-hidden="true" />
      <StarCanvas />
      <CursorDot />
      <Marquee />
      <Navbar activeSection={activeSection} onNavigate={scrollToSection} />

      <Hero        isVisible={isVisible.home}        onNavigate={scrollToSection} />
      <About />
      <Skills      isVisible={isVisible.skills} />
      <Projects    isVisible={isVisible.projects} />
      <Timeline    isVisible={isVisible.experience} />
      <Credentials isVisible={isVisible.credentials} />
      <Fun         isVisible={isVisible.fun} />
      <Contact     isVisible={isVisible.contact} />
      <Footer />

      <TweaksPanel />
    </div>
  );
}

export default App;
